import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Ban, Bot, Building2, ChevronLeft, ChevronRight, CreditCard, FileText, GraduationCap, Home, Library, LineChart, MailPlus, ScrollText, ShieldCheck, Tag, Tags, ToggleLeft, UserCheck, UsersRound, X, type LucideIcon } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import { useSidebarDrawer } from "@/components/SidebarDrawerContext";
import { cn } from "@/lib/utils";

import { endSuperAdminSession, getSuperAdminEmail } from "./session";

type NavItem = { label: string; to: string; icon: LucideIcon };
type NavSection = { heading: string; items: NavItem[] };

const SECTIONS: NavSection[] = [
  {
    heading: "Overview",
    items: [{ label: "Home", to: "/super-admin/home", icon: Home }],
  },
  {
    heading: "Platform",
    items: [
      { label: "Organizations", to: "/super-admin/orgs", icon: Building2 },
      // TKT-00124: the industry list every listing is filed under. Was seed data
      // with no screen, so adding a category meant hand-written SQL.
      { label: "Listing Categories", to: "/super-admin/listing-categories", icon: Tags },
      { label: "Resources", to: "/super-admin/resources", icon: Library },
      // BL Academy: the YouTube lessons buyers and advisors learn from.
      { label: "BL Academy", to: "/super-admin/academy", icon: GraduationCap },
      { label: "Chatbot Config", to: "/super-admin/chatbot", icon: Bot },
      { label: "Module Visibility", to: "/super-admin/modules", icon: ToggleLeft },
      // Who gets BL's internal alert emails. Lived in app/config.py as hardcoded
      // constants until this module took ownership of the routing.
      { label: "Notification Recipients", to: "/super-admin/notification-recipients", icon: MailPlus },
    ],
  },
  {
    // Both moved here from the portal admin console: publishing an NDA forces
    // every user to re-sign, and a release note is a platform-wide
    // announcement. The portal keeps read-only views of each.
    heading: "Governance",
    items: [
      { label: "NDA Management", to: "/super-admin/nda", icon: ScrollText },
      { label: "Release Notes", to: "/super-admin/release-notes", icon: FileText },
    ],
  },
  {
    // Read-only reporting, kept apart from the Billing section below: nothing
    // here changes anything, it only reports what Stripe and the activity
    // rollup already know.
    heading: "Analytics",
    items: [
      { label: "Finance", to: "/super-admin/finance", icon: LineChart },
      // The portal admin console nests these two under an expandable
      // "Utilization". This sidebar renders flat links only, so they are listed
      // flat rather than duplicating AppSidebar's group machinery for two rows.
      { label: "Buyer Utilization", to: "/super-admin/utilization/buyers", icon: UsersRound },
      { label: "Advisor Utilization", to: "/super-admin/utilization/advisors", icon: UserCheck },
    ],
  },
  {
    heading: "Billing",
    items: [
      { label: "Promo Codes", to: "/super-admin/promo-codes", icon: Tag },
      { label: "Tiers", to: "/super-admin/tiers", icon: CreditCard },
    ],
  },
];

const STORAGE_KEY = "superAdminSidebarCollapsed";

/**
 * Sidebar for the super admin shell. Mirrors AppSidebar's look, but stands on
 * its own: the shared one reads the signed-in user from AuthContext and signs
 * out through it, and the super admin session is the hardcoded one in
 * credentials.ts — routing sign-out through AuthContext would leave the super
 * admin session intact and the guard would let them straight back in.
 */
export default function SuperAdminSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const drawer = useSidebarDrawer();

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const handleSignOut = () => {
    endSuperAdminSession();
    navigate("/super-admin/sign-in", { replace: true });
  };

  // Longest matching path wins, so a parent item doesn't also light up when a
  // more specific child route is the real match.
  const bestMatch = SECTIONS.flatMap((s) => s.items.map((i) => i.to))
    .filter((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];

  /* The nav body — shared verbatim by the desktop rail and the mobile drawer.
     `isCollapsed` is a parameter, not the state above, because the drawer is
     always expanded. */
  const panel = (isCollapsed: boolean, inDrawer = false) => (
      <div className="flex h-full flex-col [font-family:var(--font-display)]">
        {/* Header row aligns with the navbar height; logo centered. */}
        <div className={cn("flex h-16 items-center px-3", inDrawer ? "justify-start pr-12" : "justify-center")}>
          <Link to="/super-admin/home" className="flex min-w-0 items-center justify-center">
            {isCollapsed ? (
              <img src="/logo_mark.png" alt="BusinessLocating" className="h-10 w-10 object-contain" />
            ) : (
              <BrandLogo className="h-12 w-auto" />
            )}
          </Link>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-2 py-4">
          {SECTIONS.map((section) => (
            <div key={section.heading}>
              {!isCollapsed && (
                <div className="px-3 pb-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  {section.heading}
                </div>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const active = item.to === bestMatch;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      title={isCollapsed ? item.label : undefined}
                      className={cn(
                        "group flex items-center gap-2.5 rounded-lg py-2 text-sm font-medium transition-colors duration-200",
                        isCollapsed ? "justify-center px-0" : "px-3",
                        active
                          ? "bg-gradient-to-r from-primary/25 to-primary/5 font-semibold text-primary shadow-[inset_2px_0_0_0_currentColor]"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      )}
                    >
                      <Icon className={cn("h-4 w-4 shrink-0", active ? "text-primary" : "text-muted-foreground")} />
                      {!isCollapsed && <span className="truncate">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Account + sign out — pinned to the bottom, like the other shells. */}
        <div className={cn("shrink-0 space-y-2 py-3", isCollapsed ? "px-2" : "px-3")}>
          <div
            className={cn(
              "flex items-center rounded-xl bg-accent/70",
              isCollapsed ? "justify-center p-1.5" : "gap-2.5 p-2.5"
            )}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <ShieldCheck className="h-4 w-4" />
            </div>
            {!isCollapsed && (
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold leading-tight text-foreground">Super Admin</div>
                <div className="truncate text-xs text-muted-foreground">{getSuperAdminEmail()}</div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            title={isCollapsed ? "Sign Out" : undefined}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl border border-border/80 bg-card py-2 text-sm font-semibold text-rose-600 transition-colors duration-200 hover:border-rose-500/40 hover:bg-rose-500/10",
              isCollapsed ? "px-0" : "px-3"
            )}
          >
            <Ban className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </div>
  );

  return (
    <>
      <aside
        className={cn(
          "sticky top-0 z-40 hidden h-screen shrink-0 bg-card/60 transition-[width] duration-200 lg:block",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute left-full top-20 z-[60] ml-2 flex h-8 w-8 items-center justify-center rounded-full border border-primary bg-gold-gradient text-primary-foreground shadow-md transition-[filter] hover:brightness-[1.06]"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>

        {panel(collapsed)}
      </aside>

      {/* Mobile: the rail above is hidden below `lg` and the navbar carried no
          menu of its own, so a super admin on a phone could open a page and
          then never navigate off it. The navbar's hamburger opens this. */}
      {drawer?.open && (
        <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-foreground/50 backdrop-blur-[2px]"
            onClick={drawer.close}
          />
          <div className="absolute inset-y-0 left-0 flex w-[17.5rem] max-w-[86vw] flex-col overflow-hidden border-r border-border/70 bg-card shadow-2xl">
            <button
              type="button"
              onClick={drawer.close}
              aria-label="Close navigation"
              className="absolute right-2 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-lg border border-border/80 bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
            {panel(false, true)}
          </div>
        </div>
      )}
    </>
  );
}